import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { ConfigOption } from '../types';
import { assetUrl } from '../api';
import { formatOptionName } from '../utils/format';
import { DEFAULT_LANG, Lang } from '../config/locale';

interface OptionCardProps {
  option: ConfigOption;
  selected: boolean;
  onClick: () => void;
  imageUrl?: string;
  lang?: Lang;
}

const OptionCard: React.FC<OptionCardProps> = ({ option, selected, onClick, imageUrl, lang = DEFAULT_LANG }) => {
  const isKo = lang === 'ko';
  const imgSrc = imageUrl ?? option.image_url;

  return (
    <Card $selected={selected} onClick={onClick}>
      <ImageArea $selected={selected}>
        {imgSrc && <OptionImg src={assetUrl(imgSrc)} alt={option.option_name} loading="lazy" />}
        {selected && <CheckBadge>✓</CheckBadge>}
      </ImageArea>
      <InfoArea>
        <OptionName $selected={selected}>{formatOptionName(isKo ? option.option_name : (option.option_name_en || option.option_name))}</OptionName>
        {(isKo ? option.description : (option.description_en || option.description)) && (
          <OptionDesc>{isKo ? option.description : (option.description_en || option.description)}</OptionDesc>
        )}
      </InfoArea>
    </Card>
  );
};

const Card = styled.div<{ $selected: boolean }>`
  background: ${theme.colors.white};
  border-radius: ${theme.layout.cardRadius};
  border: 2px solid ${({ $selected }) => $selected ? theme.colors.primary : theme.colors.border};
  cursor: pointer;
  transition: all 0.2s;
  overflow: hidden;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const ImageArea = styled.div<{ $selected: boolean }>`
  position: relative;
  height: 200px;
  background: ${({ $selected }) => $selected ? theme.colors.primaryLight : '#FAFAFA'};
  transition: background 0.2s;
`;

const OptionImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 12px;
`;

const CheckBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
`;

const InfoArea = styled.div`
  padding: 20px;
`;

const OptionName = styled.div<{ $selected?: boolean }>`
  font-size: 20px;
  font-weight: 600;
  color: ${({ $selected }) => $selected ? theme.colors.success : theme.colors.textPrimary};
  margin-bottom: 6px;
  transition: color 0.2s;
`;

const OptionDesc = styled.div`
  font-size: 14px;
  color: ${theme.colors.textSecondary};
  margin-bottom: 12px;
  line-height: 1.4;
`;

export default OptionCard;
